import 'react-grid-layout/css/styles.css';

import React, { forwardRef } from 'react';

import { GetVisualizationResponse, LayoutItem } from '@blockworks/platform/api/research/studio';
import { Bit } from '@blockworks/ui/factory';
import { cn } from '@blockworks/ui/system';

import { GridItemContent } from './grid-item-content';
import { GridItemPlaceholder } from './grid-item-placeholder';

type DashboardGridItemProps = React.PropsWithChildren<{
    /** should not be used directly. This is the classname prop that gets cloned from react-grid-layout,
     * and we added this type so we can destructure it and pass it to a `cn` helper to combine with custom classnames
     */
    className?: string;
    item: LayoutItem;
    visualization?: GetVisualizationResponse;
    resizingItem?: string;
    resizingItemHeight?: number;
}>;

const DashboardGridItem = forwardRef<HTMLDivElement, DashboardGridItemProps>(function DashboardItem(
    {
        className,
        item,
        /** children is managed by react-grid-layout and is required to render resize handles */
        children,
        /** `rest` consists of props managed by react-grid-layout */
        visualization,
        resizingItem,
        resizingItemHeight,
        ...rest
    },
    ref,
) {
    return (
        <Bit.div
            /** this `key` prop is not for react. `key` is required for react-grid-layout to work */
            key={item.i}
            data-id={item.i}
            ref={ref}
            display="flex"
            flexDirection="column"
            border={1}
            borderRadius="sm"
            className={cn('bg-surface', 'scroller', className)}
            {...rest}
        >
            {resizingItem === item.i ? (
                <GridItemPlaceholder resizingItemHeight={resizingItemHeight ?? item.h} />
            ) : (
                <GridItemContent item={item} />
            )}
            {children}
        </Bit.div>
    );
});

export { DashboardGridItem };
